import React, {useState} from "react";
import {observer} from "mobx-react";
import GeneralDialog from "./GeneralDialog";
import {useStores} from "../../Hooks";
import List from "@mui/material/List";
import {
    Checkbox,
    IconButton,
    Select,
    FormControl,
    MenuItem,
    InputLabel,
    TextField,
    Table,
    TableRow,
    TableCell, TableBody
} from "@mui/material";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {Stop} from "@mui/icons-material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ConditionalComponent from "../Shared/ConditionalComponent";
import DynamicAvatar from "../Shared/DynamicAvatar";
import Utility from "../../Utility";
import {SelectPeriodicValues} from "./SubWorkspacesManagmentModals/GenerateReportsModal/SelectPeriodicValues";
import {MembersRequester} from "../Domain/Groups/MembersRequester";

const toDisplayFields = (user) => ({
    fullName: [user.FirstName, user.LastName].filter(Boolean).join(' '),
    avatarUser: {Name: user.FirstName, LastName: user.LastName},
});

const initFieldsList = [
    ['knowledgeBaseItem', true],
    ['userName', true],
    ['userEmail', true],
    ['language', true],
    ['project', true],
    ['numberOfStepsViewed', true],
    ['duration', true],
    ['date', true],
    ['deviceType', true],
    ['sessionType', true],
    ['taskReference', true],
    ['modeName', true],
    ['isTest', true],
    ['score', true],
    ['appFeedbackScore', true],
    ['instructionsFeedbackScore', true],
];

const AutoGenerateReportsModal = observer(() => {
    const {modalStore, generalStore, projectStore} = useStores();

    const [fieldsMap, setFieldsMap] = useState(new Map(initFieldsList));
    const [frequency, setFrequency] = useState("");
    const [periodType, setPeriodType] = useState(2);
    const [weekValue, setWeekValue] = useState(0);
    const [dayValue, setDayValue] = useState(1);
    const [monthValue, setMonthValue] = useState(0);
    const [attachedUserEmail, setAttachedUserEmail] = useState("");
    const [isPaused, setIsPaused] = useState(false);
    const [projectUsers, setProjectUsers] = useState([]);

    const saveSettings = () => {
        const settingsObj = {
            projectRef: projectStore.currentProject?.ProjectIdentifier,
            frequency: frequency || 0,
            periodType,
            weekValue,
            dayValue,
            monthValue,
            attachedUserEmail,
            fieldsIncluded: [],
            isPaused,
        }
        fieldsMap.forEach((fieldState, fieldKey) => {
            if (!!fieldState) {
                settingsObj.fieldsIncluded.push(fieldKey)
            }
        });

        generalStore.saveAutoReportSettings(settingsObj).then(success => {
            if (!success) {
                modalStore.showErrorMessage(generalStore.translate('problemSavingAutoReportSettings'));
            }
        }).catch(e => {
            console.error(e);
            modalStore.showErrorMessage(generalStore.translate('problemSavingAutoReportSettings'));
        });
        onClose();
    }

    const handleSelectField = listKey => {
        const updatedFieldsMap = new Map(fieldsMap);
        const currentSetting = fieldsMap.get(listKey);
        updatedFieldsMap.set(listKey, !currentSetting);
        setFieldsMap(updatedFieldsMap);
    }

    const loadProjectUsers = async () => {
        const projectId = projectStore.currentProject?.ProjectIdentifier;
        if (!projectId) {
            setProjectUsers([]);
            return;
        }
        const members = await MembersRequester.getAutoReportRecipients(generalStore.workspace, projectId);
        const currentUserId = generalStore.userDetails?._id;
        setProjectUsers((members || []).filter(m => m._id !== currentUserId));
    }

    const getIsStopped = () => !frequency || (!periodType && periodType !== 0);

    const renderActionButtons = () => {
        const isStopped = getIsStopped();
        return (
            <div className="report-generation-action-buttons">
                <ConditionalComponent if={!isStopped}>
                    <ConditionalComponent if={!isPaused}>
                        <IconButton
                            onClick={() => setIsPaused(true)}
                        >
                            <PauseIcon className='report-pause-btn'/>
                        </IconButton>
                    </ConditionalComponent>
                    <ConditionalComponent if={isPaused}>
                        <IconButton
                            onClick={() => setIsPaused(false)}
                        >
                            <PlayArrowIcon className='report-play-btn'/>
                        </IconButton>
                    </ConditionalComponent>
                    <IconButton
                        onClick={() => setFrequency("")}
                    >
                        <Stop className='report-stop-btn'/>
                    </IconButton>
                </ConditionalComponent>
            </div>
        )
    }

    const renderSelectAttachedUser = () => {
        return (
            <FormControl variant="outlined" className="report-select-attached-user">
                <InputLabel id="select-attached-user-label">{generalStore.translate('attachedUser')}</InputLabel>
                <Select
                    labelId="select-attached-user-label"
                    id="select-attached-user"
                    value={attachedUserEmail}
                    onChange={(e) => setAttachedUserEmail(e.target.value)}
                    label={generalStore.translate('attachedUser')}
                    renderValue={(selected) => selected}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {projectUsers.map(user => {
                        const {fullName, avatarUser} = toDisplayFields(user);
                        return (
                            <MenuItem
                                key={user._id}
                                className={'user-list-item' + (attachedUserEmail === user.Email ? ' disabled' : '')}
                                value={user.Email}
                            >
                                <DynamicAvatar user={avatarUser} imageSrc={user.UserIcon}/>
                                <div className='user-name-email-container'>
                                    <div className='user-name-container'>{fullName}</div>
                                    <div className='user-email-container'>{user.Email}</div>
                                </div>
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
        )
    }

    const renderSelectReportFields = () => {
        const keyList1 = [...fieldsMap.keys()];
        const keyList2 = keyList1.splice(Math.ceil(fieldsMap.size / 2));

        return (
            <div className="report-fields-container">
                <div className="report-fields-title">
                    {generalStore.translate('fieldsToInclude')}
                </div>
                <div className="report-fields-selection">
                    <List className="report-fields">
                        {keyList1.map(listKey =>
                            <ListItem
                                key={listKey}
                                button
                                onClick={() => handleSelectField(listKey)}
                            >
                                <Checkbox
                                    checked={fieldsMap.get(listKey)}
                                    color='primary'
                                    inputProps={{'aria-labelledby': listKey}}
                                    disableRipple
                                />
                                <ListItemText
                                    primary={generalStore.translate(listKey)}
                                    style={{overflowWrap: "anywhere"}}
                                />
                            </ListItem>
                        )}
                    </List>
                    <List className="report-fields">
                        {keyList2.map(listKey =>
                            <ListItem
                                key={listKey}
                                button
                                onClick={() => handleSelectField(listKey)}
                            >
                                <Checkbox
                                    checked={fieldsMap.get(listKey)}
                                    color='primary'
                                    inputProps={{'aria-labelledby': listKey}}
                                    disableRipple
                                />
                                <ListItemText
                                    primary={generalStore.translate(listKey)}
                                    style={{overflowWrap: "anywhere"}}
                                />
                            </ListItem>
                        )}
                    </List>
                </div>
            </div>
        )
    }

    const renderBody = () => {
        return (
            <div className="body-container">
                <div className="auto-generation-settings-container">
                    <SelectPeriodicValues dayValue={dayValue} monthValue={monthValue} weekValue={weekValue} frequency={frequency} periodType={periodType} setWeekValue={setWeekValue}
                    setMonthValue={setMonthValue} setDayValue={setDayValue} setFrequency={setFrequency} setPeriodType={setPeriodType} />
                    <div className="auto-generation-settings">
                        {renderActionButtons()}
                        {renderSelectAttachedUser()}
                    </div>
                </div>
                {renderSelectReportFields()}
            </div>
        );
    }

    const onEnter = async () => {
        await loadProjectUsers();
        const autoReports = generalStore.userDetails?.Domains?.[generalStore.workspace]?.autoReports;
        if (!autoReports?.length) {
            return
        }

        const reportSettings = autoReports.find(reportSetting => reportSetting.ProjectRef === projectStore.currentProject?.ProjectIdentifier);
        if (!reportSettings) {
            return
        }

        const updatedFieldsMap = new Map();
        fieldsMap.forEach((ignored, fieldKey) => {
            updatedFieldsMap.set(fieldKey, reportSettings?.FieldsIncluded.includes(fieldKey))
        });
        setFieldsMap(updatedFieldsMap);
        setFrequency(reportSettings.Frequency || "");
        setPeriodType(Utility.getValueEvenIf0(reportSettings.PeriodType, 2));
        setWeekValue(reportSettings.WeekValue || 0);
        setDayValue(Utility.getValueEvenIf0(reportSettings.DayValue, 1));
        setMonthValue(reportSettings.MonthValue || 0);
        setAttachedUserEmail(reportSettings.AttachedUserEmail || "");
        setIsPaused(!!reportSettings.IsPaused);
    }

    const resetSettings = () => {
        setFieldsMap(new Map(initFieldsList));
        setFrequency("");
        setPeriodType(2);
        setWeekValue(0);
        setDayValue(1);
        setMonthValue(0);
        setAttachedUserEmail("");
        setIsPaused(false);
        setProjectUsers([]);
    }


    const onClose = () => {
        modalStore.isAutoGenerateReportsModalOpen = false;
        resetSettings();
    }

    return (
        <GeneralDialog
            openControl='isAutoGenerateReportsModalOpen'
            className='procedure-option-dialog auto-generate-reports-dialog'
            onEnter={onEnter}
            title={generalStore.translate('analyticsAutomaticReportSettings')}
            body={renderBody()}
            isConfirmDisabled={false}
            onConfirm={saveSettings}
            confirmText={generalStore.translate('save')}
            specialOnClose={onClose}
        />
    );
})

export default AutoGenerateReportsModal;
